"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export function useBroadcastChannel<T>(
  name: `venturu_${string}`,
  handler: (message: T) => void | Promise<void>,
) {
  const [message, setMessage] = useState<T | null>(null);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const channelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;

    const channel = new BroadcastChannel(name);
    channelRef.current = channel;

    function handleMessage(e: MessageEvent<T>) {
      setMessage(e.data);
      handlerRef.current(e.data);
    }

    channel.addEventListener("message", handleMessage);

    return () => {
      channel.removeEventListener("message", handleMessage);
      channel.close();
      channelRef.current = null;
    };
  }, [name]);

  const postMessage = useCallback((msg: T) => {
    try {
      channelRef.current?.postMessage(msg);
    } catch {
      // channel is already closed
    }
  }, []);

  const closeChannel = useCallback(() => {
    try {
      channelRef.current?.close();
      channelRef.current = null;
    } catch {
      // channel is already closed
    }
  }, []);

  return {
    message,
    postMessage,
    closeChannel,
  };
}
